import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Button,
  Box,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Grid,
  Chip,
  IconButton,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import api from '../api';
import { getRoleFromToken } from '../utils/auth';
import { useNavigate } from 'react-router-dom';

function SessionManagement() {
  // restrict access to superadmin only
  const role = getRoleFromToken ? getRoleFromToken() : null;
  const navigate = useNavigate();
  useEffect(() => {
    if (role !== 'superadmin') {
      navigate('/dashboard');
    }
  }, [role, navigate]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentSession, setCurrentSession] = useState({
    name: '',
    startDate: '',
    endDate: '',
    status: 'active',
  });

  useEffect(() => {
    fetchSessions();
  }, []);

  const fetchSessions = async () => {
    try {
      const res = await api.get('/api/sessions');
      setSessions(res.data);
      setLoading(false);
    } catch (err) {
      setError('Failed to fetch sessions');
      setLoading(false);
    }
  };

  const handleOpenDialog = (session = null) => {
    if (session) {
      setEditMode(true);
      setCurrentSession({
        ...session,
        startDate: new Date(session.startDate).toISOString().split('T')[0],
        endDate: new Date(session.endDate).toISOString().split('T')[0],
      });
    } else {
      setEditMode(false);
      setCurrentSession({
        name: '',
        startDate: '',
        endDate: '',
        status: 'active',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentSession({
      name: '',
      startDate: '',
      endDate: '',
      status: 'active',
    });
  };

  const handleSave = async () => {
    setError('');
    setSuccess('');

    if (!currentSession.name || !currentSession.startDate || !currentSession.endDate) {
      setError('All fields are required');
      return;
    }

    try {
      if (editMode) {
        await api.put(`/api/sessions/${currentSession._id}`, currentSession);
        setSuccess('Session updated successfully');
      } else {
        await api.post('/api/sessions', currentSession);
        setSuccess('Session created successfully');
      }
      fetchSessions();
      handleCloseDialog();
    } catch (err) {
      setError('Failed to save session');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this session?')) return;

    try {
      await api.delete(`/api/sessions/${id}`);
      setSuccess('Session deleted successfully');
      fetchSessions();
    } catch (err) {
      setError('Failed to delete session');
    }
  };

  const columns = [
    {
      field: 'name',
      headerName: 'Session Name',
      flex: 2,
      minWidth: 200,
    },
    {
      field: 'startDate',
      headerName: 'Start Date',
      flex: 1,
      minWidth: 130,
      renderCell: (params) => {
        if (!params.value) return '—';
        try {
          const date = new Date(params.value);
          if (isNaN(date.getTime())) return '—';
          return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
          });
        } catch {
          return '—';
        }
      },
    },
    {
      field: 'endDate',
      headerName: 'End Date',
      flex: 1,
      minWidth: 130,
      renderCell: (params) => {
        if (!params.value) return '—';
        try {
          const date = new Date(params.value);
          if (isNaN(date.getTime())) return '—';
          return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
          });
        } catch {
          return '—';
        }
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 1,
      minWidth: 100,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={params.value === 'active' ? 'success' : 'default'}
          size="small"
        />
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 1,
      minWidth: 150,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <IconButton
            size="small"
            color="primary"
            onClick={() => handleOpenDialog(params.row)}
          >
            <EditIcon />
          </IconButton>
          <IconButton
            size="small"
            color="error"
            onClick={() => handleDelete(params.row._id)}
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      ),
    },
  ];

  const activeCount = sessions.filter((s) => s.status === 'active').length;
  const inactiveCount = sessions.filter((s) => s.status === 'inactive').length;

  return (
    <Container maxWidth="lg" sx={{ mt: { xs: 2, md: 4 }, mb: 4, px: { xs: 2, sm: 3 } }}>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', md: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'flex-start', md: 'center' }, 
        mb: { xs: 3, md: 4 },
        pb: 2,
        gap: { xs: 2, md: 0 },
        borderBottom: '2px solid',
        borderColor: 'primary.main'
      }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#2c3e50', fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}>
          Session Management
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          fullWidth={false}
          sx={{
            borderRadius: 2,
            px: 3,
            py: 1,
            fontWeight: 600,
            width: { xs: '100%', sm: 'auto' },
            fontSize: { xs: '0.875rem', md: '1rem' },
            boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 6px 16px rgba(25, 118, 210, 0.4)',
            },
            transition: 'all 0.3s ease',
          }}
        >
          Create Session
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
            color: 'white',
            borderRadius: 3,
            boxShadow: '0 4px 20px rgba(102, 126, 234, 0.3)',
            transition: 'transform 0.3s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
            }
          }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>Total Sessions</Typography>
              <Typography variant="h3" sx={{ fontWeight: 700 }}>{sessions.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)', 
            color: 'white',
            borderRadius: 3,
            boxShadow: '0 4px 20px rgba(56, 239, 125, 0.3)',
            transition: 'transform 0.3s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
            }
          }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>Active</Typography>
              <Typography variant="h3" sx={{ fontWeight: 700 }}>{activeCount}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, #757f9a 0%, #d7dde8 100%)', 
            color: 'white',
            borderRadius: 3,
            boxShadow: '0 4px 20px rgba(117, 127, 154, 0.3)',
            transition: 'transform 0.3s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
            }
          }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>Inactive</Typography>
              <Typography variant="h3" sx={{ fontWeight: 700 }}>{inactiveCount}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Sessions Table */}
      <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
        <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0', bgcolor: '#fafafa' }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#2c3e50' }}>
            All Sessions
          </Typography>
        </Box>
        <Box sx={{ height: 550, width: '100%' }}>
          <DataGrid
            rows={sessions}
            columns={columns}
            getRowId={(row) => row._id}
            pageSize={10}
            rowsPerPageOptions={[10, 25, 50]}
            loading={loading}
            disableSelectionOnClick
            sx={{
              border: 'none',
              '& .MuiDataGrid-cell': {
                borderBottom: '1px solid #f0f0f0',
              },
              '& .MuiDataGrid-columnHeaders': {
                bgcolor: '#f8f9fa',
                borderBottom: '2px solid #dee2e6',
                fontWeight: 600,
              },
              '& .MuiDataGrid-row:hover': {
                bgcolor: '#f8f9ff',
              },
            }}
          />
        </Box>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: '#2c3e50',
          fontWeight: 700,
          fontSize: '1.5rem',
          pb: 2,
        }}>
          {editMode ? 'Edit Session' : 'Create New Session'}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1 }}>
            <TextField
              label="Session Name"
              fullWidth
              value={currentSession.name}
              onChange={(e) => setCurrentSession({ ...currentSession, name: e.target.value })}
              placeholder="e.g., 2024/2025 First Semester"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            />
            <TextField
              label="Start Date"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={currentSession.startDate}
              onChange={(e) => setCurrentSession({ ...currentSession, startDate: e.target.value })}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            />
            <TextField
              label="End Date"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={currentSession.endDate}
              onChange={(e) => setCurrentSession({ ...currentSession, endDate: e.target.value })}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            />
            <FormControl fullWidth sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              },
            }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={currentSession.status}
                label="Status"
                onChange={(e) => setCurrentSession({ ...currentSession, status: e.target.value })}
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1 }}>
          <Button 
            onClick={handleCloseDialog}
            sx={{
              borderRadius: 2,
              px: 3,
              fontWeight: 600,
            }}
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleSave}
            sx={{
              borderRadius: 2,
              px: 3,
              fontWeight: 600,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
              },
            }}
          >
            {editMode ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default SessionManagement;
